import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "continentField",
    "countryField",
    "regionField",
    "regionDropdown",
    "countryDropdown",
    "continentButton",
  ];

  static values = {
    continentId: String,
    continentName: String,
    countryId: String,
    countryName: String,
    regionId: String,
    regionName: String,
  };

  connect() {
    // First update the continent buttons right away
    if (this.continentIdValue) {
      this.updateContinentButtons(this.continentIdValue);
    } else {
      this.updateContinentButtons("");
    }

    if (this.regionIdValue) {
      // First load countries for the continent
      this.loadCountriesByContinent(
        this.continentIdValue,
        this.continentNameValue
      ).then(() => {
        // Then set the country in the dropdown
        this.setSelectedCountry(this.countryIdValue, this.countryNameValue);
        // Finally load regions and select the region
        this.loadRegionsByCountry(
          this.countryIdValue,
          this.countryNameValue
        ).then(() => {
          this.setSelectedRegion(this.regionIdValue, this.regionNameValue);
        });
      });
      return;
    }

    if (this.countryIdValue) {
      // First load countries for the continent
      this.loadCountriesByContinent(
        this.continentIdValue,
        this.continentNameValue
      ).then(() => {
        // Then set the country in the dropdown and load regions
        this.setSelectedCountry(this.countryIdValue, this.countryNameValue);
        this.loadRegionsByCountry(this.countryIdValue, this.countryNameValue);
      });
      return;
    }

    if (this.continentIdValue) {
      this.loadCountriesByContinent(
        this.continentIdValue,
        this.continentNameValue
      );
      return;
    }
    this.loadCountriesByContinent("");
  }

  setSelectedCountry(countryId, countryName) {
    if (!countryId) return;

    const countryDropdown = this.countryDropdownTarget;
    const options = countryDropdown.options;

    for (let i = 0; i < options.length; i++) {
      if (
        options[i].value === countryId ||
        options[i].dataset.countryId === countryId
      ) {
        countryDropdown.selectedIndex = i;
        this.countryFieldTarget.value = countryId;
        break;
      }
    }
  }

  setSelectedRegion(regionId, regionName) {
    if (!regionId) return;

    const regionDropdown = this.regionDropdownTarget;
    const options = regionDropdown.options;

    for (let i = 0; i < options.length; i++) {
      if (
        options[i].value === regionId ||
        options[i].dataset.regionId === regionId
      ) {
        regionDropdown.selectedIndex = i;
        this.regionFieldTarget.value = regionId;
        break;
      }
    }
  }

  selectContinent(event) {
    const continentName = event.currentTarget.dataset.continentName;
    const continentId = event.currentTarget.dataset.continentId;

    // Update the continentIdValue to match what was clicked
    if (continentId === "") {
      this.continentIdValue = "";
    }

    this.loadCountriesByContinent(continentId, continentName);
  }

  selectCountry(event) {
    const select = event.currentTarget;
    const option = select.options[select.selectedIndex];
    const countryId = option.dataset.countryId || option.value;
    const countryName =
      option.dataset.countryName || option.text || "All Countries";

    // Update the hidden field
    this.countryFieldTarget.value = countryId;

    // Load regions for this country
    this.loadRegionsByCountry(countryId, countryName);
  }

  selectRegion(event) {
    const select = event.currentTarget;
    const option = select.options[select.selectedIndex];
    const regionId = option.dataset.regionId || option.value;
    const regionName =
      option.dataset.regionName || option.text || "All Regions";

    // Update the hidden field
    this.regionFieldTarget.value = regionId;
  }

  loadCountriesByContinent(continentId, continentName) {
    // Update buttons
    this.updateContinentButtons(continentId);

    // Set the hidden field value
    this.continentFieldTarget.value = continentId;

    // Reset region dropdown
    this.resetRegionDropdown();

    // Show loading state in country dropdown
    const countryDropdown = this.countryDropdownTarget;
    countryDropdown.disabled = true;
    countryDropdown.innerHTML =
      '<option value="">Loading countries...</option>';

    // Make API call to get countries by continent
    return fetch(
      `/regions/countries_by_continent?continent=${encodeURIComponent(
        continentId
      )}`
    )
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((countries) => {
        // Reset the country value
        this.countryFieldTarget.value = "";

        // Clear the dropdown
        countryDropdown.innerHTML = "";

        // Add the "All Countries" option
        const allCountriesOption = document.createElement("option");
        allCountriesOption.value = "";
        allCountriesOption.dataset.countryId = "";

        // Set appropriate text based on whether this is worldwide or a specific continent
        if (continentId === "") {
          allCountriesOption.textContent = "All Countries";
        } else {
          allCountriesOption.textContent = `All Countries in ${
            continentName || "this continent"
          }`;
        }

        countryDropdown.appendChild(allCountriesOption);

        // Add countries to dropdown
        if (countries.length > 0) {
          countries.forEach((country) => {
            const option = document.createElement("option");
            option.value = country.id;
            option.dataset.countryId = country.id;
            option.dataset.countryName = country.name;
            option.textContent = `${country.name} (${country.continent.name})`;
            countryDropdown.appendChild(option);
          });
        } else if (continentId) {
          const option = document.createElement("option");
          option.disabled = true;
          option.textContent = `No Countries in ${continentName}`;
          countryDropdown.appendChild(option);
        }

        // Re-enable the dropdown
        countryDropdown.disabled = false;
        return countries;
      })
      .catch((error) => {
        // Error fetching countries
        countryDropdown.innerHTML =
          '<option value="">Error loading countries</option>';
        countryDropdown.disabled = false;
        return [];
      });
  }

  loadRegionsByCountry(countryId, countryName = "Country") {
    // Reset the region field
    this.regionFieldTarget.value = "";

    // Get UI elements
    const regionDropdown = this.regionDropdownTarget;

    // Reset region dropdown
    regionDropdown.innerHTML = "";

    if (!countryId) {
      regionDropdown.disabled = true;
      regionDropdown.innerHTML =
        '<option value="">Select Country First</option>';
      return Promise.resolve([]);
    }

    // Show loading state
    regionDropdown.disabled = true;
    regionDropdown.innerHTML = '<option value="">Loading regions...</option>';

    // Fetch regions for the selected country
    return fetch(`/regions?country_id=${countryId}`)
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((regions) => {
        // Reset dropdown
        regionDropdown.innerHTML = "";

        // Add default option
        const allRegionsOption = document.createElement("option");
        allRegionsOption.value = "";
        allRegionsOption.dataset.regionId = "";
        allRegionsOption.textContent = `All Regions in ${countryName}`;
        regionDropdown.appendChild(allRegionsOption);

        // Add regions
        if (regions.length > 0) {
          regions.forEach((region) => {
            const option = document.createElement("option");
            option.value = region.id;
            option.dataset.regionId = region.id;
            option.dataset.regionName = region.name;
            option.textContent = region.name;
            regionDropdown.appendChild(option);
          });
          regionDropdown.disabled = false;
        } else {
          const option = document.createElement("option");
          option.disabled = true;
          option.textContent = `No regions found in ${countryName}`;
          regionDropdown.appendChild(option);
        }

        return regions;
      })
      .catch((error) => {
        // Error fetching regions
        regionDropdown.innerHTML =
          '<option value="">Error loading regions</option>';
        regionDropdown.disabled = true;
        return [];
      });
  }

  resetRegionDropdown() {
    const regionDropdown = this.regionDropdownTarget;
    regionDropdown.innerHTML = '<option value="">Select Country First</option>';
    regionDropdown.disabled = true;
    this.regionFieldTarget.value = "";
  }

  updateContinentButtons(selectedContinentId) {
    this.continentButtonTargets.forEach((button) => {
      const buttonContinentId = button.dataset.continentId;
      if (buttonContinentId === selectedContinentId) {
        button.classList.remove("inactive");
        button.classList.remove("btn-share");
        button.classList.add("active");
        button.classList.add("btn-play");
      } else {
        button.classList.remove("active");
        button.classList.remove("btn-play");
        button.classList.add("inactive");
        button.classList.add("btn-share");
      }
    });
  }
}
